import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const CART_KEY = "tatva-hills-cart-v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = window.localStorage.getItem(CART_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    window.localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (product, quantity = product.minQuantity || 1, packSize = product.packSize) => {
    if (product.isAvailable === false) {
      toast.error(`${product.name} is out of season`, {
        description: "This harvest is currently unavailable.",
      });
      return;
    }
    const safeQuantity = Math.max(quantity, product.minQuantity || 1);
    setItems((current) => {
      const existing = current.find((item) => item.id === product.id && item.packSize === packSize);
      if (existing) {
        return current.map((item) =>
          item.id === product.id && item.packSize === packSize
            ? { ...item, quantity: item.quantity + safeQuantity }
            : item,
        );
      }
      return [
        ...current,
        {
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          priceLabel: product.priceLabel,
          packSize,
          image: product.image,
          quantity: safeQuantity,
          minQuantity: product.minQuantity || 1,
          isAvailable: product.isAvailable !== false,
        },
      ];
    });
    toast.success(`${product.name} added to cart`, {
      description: "Fresh harvest added to your basket.",
    });
  };

  const updateQuantity = (id, quantity, packSize) => {
    setItems((current) =>
      current.map((item) => {
        if (item.id !== id || item.packSize !== packSize) return item;
        const minimum = item.minQuantity || 1;
        return { ...item, quantity: Math.max(minimum, quantity) };
      }),
    );
  };

  const removeItem = (id, packSize) => {
    setItems((current) => current.filter((item) => !(item.id === id && item.packSize === packSize)));
  };

  const clearCart = () => setItems([]);

  const value = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    return { items, subtotal, count, addToCart, updateQuantity, removeItem, clearCart };
  }, [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);